import React, { useState, useEffect } from "react";
import axios from "axios";
import { PlusCircle, Trash2, Search } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Navbar from "./Navbar";

const NurseManager = () => {
  const [nurses, setNurses] = useState([]);
  const [newNurses, setNewNurses] = useState([
    {
      name: "",
      phone_number: "",
      center_hospital: "",
      city: "",
      state: "",
      under_dr_name: "",
      department: "",
    },
  ]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchNurses();
  }, []);

  const fetchNurses = async () => {
    try {
      const res = await axios.get("https://www.careplus.gopillz.com/nurses", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setNurses(res.data);
    } catch (err) {
      console.error("Error fetching nurses:", err);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...newNurses];
    updated[index][field] = value;
    setNewNurses(updated);
  };

  const addNurseField = () => {
    setNewNurses([
      ...newNurses,
      {
        name: "",
        phone_number: "",
        center_hospital: "",
        city: "",
        state: "",
        under_dr_name: "",
        department: "",
      },
    ]);
  };

  const removeNurseField = (index) => {
    const updated = [...newNurses];
    updated.splice(index, 1);
    setNewNurses(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const payload = {
        nurses: newNurses,
      };
      await axios.post("https://www.careplus.gopillz.com/nurses", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage("✅ Nurse(s) added successfully!");
      setNewNurses([
        {
          name: "",
          phone_number: "",
          center_hospital: "",
          city: "",
          state: "",
          department: "",
        },
      ]);
      fetchNurses();
    } catch (err) {
      setMessage("❌ Failed to add nurses. Try again.");
    }
    setLoading(false);
  };

  const filteredNurses = nurses.filter((nurse) =>
    nurse.name.toLowerCase().includes(searchTerm.toLowerCase())||
    nurse.phone_number.toLowerCase().includes(searchTerm.toLowerCase())||
    nurse.center_hospital.toLowerCase().includes(searchTerm.toLowerCase())||
    nurse.city.toLowerCase().includes(searchTerm.toLowerCase())||
    nurse.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-blue-50 min-h-screen">
      <Navbar />
      <div className="flex justify-center items-start px-4 pt-24 pb-10">
        <div className="w-full max-w-6xl space-y-10">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-blue-700 mb-2">🧑‍⚕️ Nurse Manager</h1>
            <p className="text-gray-600">Manage nurses efficiently</p>
          </div>

          {/* Search */}
          <div className="flex justify-center">
            <div className="relative w-full sm:w-1/2">
              <input
                type="text"
                placeholder="Search by nurse name,phone number,hospital,city or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-10 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
              />
              <Search className="absolute left-3 top-2.5 text-gray-500 w-5 h-5" />
            </div>
          </div>

          {/* Nurse List */}
          <section className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Existing Nurses</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredNurses.length === 0 ? (
                <p className="text-gray-500 col-span-full">No nurses found.</p>
              ) : (
                filteredNurses.map((nurse, index) => (
                  <div
                    key={index}
                    className="bg-blue-100 border border-blue-200 rounded-xl p-4 hover:shadow-lg transition"
                  >
                    <h3 className="text-lg font-semibold text-blue-900">{nurse.name}</h3>
                    <p className="text-sm text-blue-800">📞 {nurse.phone_number}</p>
                    <p className="text-sm text-blue-800">🏥 {nurse.center_hospital}</p>
                    <p className="text-sm text-blue-800">🧑‍⚕️ Under Dr. {nurse.doctor__doctor_name || "N/A"}</p>
                    <p className="text-sm text-blue-800">📍 {nurse.city}, {nurse.state}</p>
                    <p className="text-sm text-blue-800">🩺 Dept: {nurse.department}</p>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Add Nurse Form */}
          <section className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">➕ Add New Nurse(s)</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {newNurses.map((nurse, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input
                    type="text"
                    placeholder="Nurse Name"
                    value={nurse.name}
                    onChange={(e) => handleChange(index, "name", e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <PhoneInput
                    country="in"
                    enableSearch
                    value={nurse.phone_number}
                    onChange={(value) => handleChange(index, "phone_number", value)}
                    inputClass="!w-full !px-4 !py-2 !border !border-gray-300 !rounded-lg !bg-gray-50"
                    buttonClass="!border-gray-300"
                    containerClass="!w-full"
                  />
                  <input
                    type="text"
                    placeholder="Center Hospital"
                    value={nurse.center_hospital}
                    onChange={(e) => handleChange(index, "center_hospital", e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="text"
                    placeholder="City"
                    value={nurse.city}
                    onChange={(e) => handleChange(index, "city", e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={nurse.state}
                    onChange={(e) => handleChange(index, "state", e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Department"
                    value={nurse.department}
                    onChange={(e) => handleChange(index, "department", e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  {newNurses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeNurseField(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-4 mt-2">
                <button
                  type="button"
                  onClick={addNurseField}
                  className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                >
                  <PlusCircle className="w-5 h-5" /> Add Another
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  {loading ? "Saving..." : "Submit Nurses"}
                </button>
              </div>
              {message && <p className="text-blue-600 mt-2">{message}</p>}
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default NurseManager;
